import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Leaf,
  Mail,
  AlertTriangle,
} from "lucide-react";

const API_URL = window.location.origin;

export default function VerificaEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("loading"); // loading, success, already_verified, error, expired
  const [message, setMessage] = useState("");
  const [nome, setNome] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Link di verifica non valido. Assicurati di aver copiato correttamente l'URL.");
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/verify-email?token=${token}`);
        const data = await response.json();

        if (response.ok) {
          if (data.already_verified) {
            setStatus("already_verified");
          } else {
            setStatus("success");
          }
          setMessage(data.message);
          setNome(data.nome || "");
        } else {
          if (data.detail?.includes("scaduto")) {
            setStatus("expired");
          } else {
            setStatus("error");
          }
          setMessage(data.detail || "Errore durante la verifica");
        }
      } catch (error) {
        console.error("Verification error:", error);
        setStatus("error");
        setMessage("Errore di connessione. Riprova più tardi.");
      }
    };

    verifyEmail();
  }, [token]);

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <>
            <div className="w-20 h-20 mx-auto bg-teal-100 rounded-full flex items-center justify-center mb-6">
              <Loader2 className="w-10 h-10 text-teal-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Verifica in corso...</h2>
            <p className="text-slate-600">Stiamo verificando il tuo indirizzo email.</p>
          </>
        );

      case "success":
        return (
          <>
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              {nome ? `Benvenuto, ${nome}!` : "Email Verificata!"}
            </h2>
            <p className="text-slate-600 mb-6">{message}</p>
            <p className="text-sm text-slate-500 mb-6">
              Ora puoi accedere a tutti i contenuti esclusivi sulla Scienza del Benessere.
            </p>
            <div className="space-y-3">
              <Link to="/benessere">
                <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700">
                  Esplora i Contenuti
                </Button>
              </Link>
              <Link to="/benessere/articoli">
                <Button variant="outline" className="w-full">
                  Leggi gli Articoli
                </Button>
              </Link>
            </div>
          </>
        );

      case "already_verified":
        return (
          <>
            <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Email Già Verificata</h2>
            <p className="text-slate-600 mb-6">
              Il tuo indirizzo email è già stato confermato in precedenza.
            </p>
            <Link to="/benessere">
              <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700">
                Vai ai Contenuti
              </Button>
            </Link>
          </>
        );

      case "expired":
        return (
          <>
            <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-6">
              <AlertTriangle className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Link Scaduto</h2>
            <p className="text-slate-600 mb-6">
              Il link di verifica è scaduto. Registrati nuovamente per ricevere un nuovo link.
            </p>
            <Link to="/registrazione-benessere">
              <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700">
                Registrati Nuovamente
              </Button>
            </Link>
          </>
        );

      case "error":
      default:
        return (
          <>
            <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Verifica Fallita</h2>
            <p className="text-slate-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Link to="/registrazione-benessere">
                <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700">
                  Registrati Nuovamente
                </Button>
              </Link>
              <Link to="/benessere">
                <Button variant="outline" className="w-full">
                  Torna alla Home
                </Button>
              </Link>
            </div>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex flex-col" data-testid="verifica-email-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16">
            <Link to="/benessere" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-lg font-semibold text-slate-800">La Scienza del Benessere</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center shadow-xl">
          <CardContent className="pt-10 pb-8">
            {renderContent()}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-slate-500">
        <p>© 2025 ODR Academy - La Scienza del Benessere</p>
      </footer>
    </div>
  );
}
